import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { next, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { equal } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import type { Args as FormSelectArgs, OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import type {
	GetItemsMethodName as ExtendedGetItemsMethodName,
	HasItemsPropOnMetricSetup as ExtendedHasItemsPropOnMetricSetup,
	HasItemsPropOnMk as ExtendedHasItemsPropOnMk,
} from 'webapp/components/form/select-units';

import type DateRange from 'webapp/models-custom/date-range';

export type Options = OptionRaw<string>[];

// could be fake bcos of master filter (no one MetricSetup there)
interface MetricSetupCompatible {
	metricSetupDataForFetchDimensionValues: DSSetup.MetricSetupWithoutFiltersData[];
	metric?: { isGeneric?: boolean } | null;
	isDimensionSelected?: boolean;
	hasUnits?: boolean;
}

interface ArgsBase {
	metricSetupForQuery?: MetricSetupCompatible | null;
	dateRangeForQuery?: DateRange | null;
	context?: string;
	placeholder?: FormSelectArgs['placeholder'];
	isMultiple?: FormSelectArgs['isMultiple'];
	maxSelectionLength?: FormSelectArgs['maxSelectionLength'];
	isDisabled?: FormSelectArgs['isDisabled'];
	isRequired?: FormSelectArgs['isRequired'];
	showFloatlabel?: FormSelectArgs['showFloatlabel'];
	theme?: FormSelectArgs['theme'];
	size?: FormSelectArgs['size'];
	disabledItemsList?: string[];
	loadOptions?: () => Promise<Options>; // replaces the dimension values request when set
}

export interface ArgsSingle extends ArgsBase {
	selected: string | null;
	isMultiple?: false;
	onChange: (value: string | null) => void;
	beforeChange?: (
		currentValue: string,
		newValue: string,
		selectElement: JQuery<HTMLElement>,
	) => boolean;
}

export interface ArgsMultiple extends ArgsBase {
	selected: string[];
	isMultiple: true;
	onChange: (value: string[]) => void;
	beforeChange?: (
		currentValue: string[],
		newValue: string[],
		selectElement: JQuery<HTMLElement>,
	) => boolean;
}

export type Args = ArgsSingle | ArgsMultiple;

const GetItemsMethodName = 'getAttributes';
const HasItemsPropOnMk = 'isAttributed';
const HasItemsPropOnMetricSetup = 'isDimensionSelected';

export default class FormSelectAttributesComponent<T extends Args> extends Component<T> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@tracked isLoading = false;

	@tracked options: Options = [];

	uniqueId = 'select-' + guidFor(this);

	selectNode: HTMLSelectElement | null = null;

	isOnList(item: string, list: Options) {
		return !!list.find((i) => i.value === item);
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.updateOptions([], true);
	}

	get placeholder() {
		return this.args.placeholder || `Select value${this.args.isMultiple ? 's' : ''}`;
	}

	get floatlabelText() {
		return `Dimension value${this.args.isMultiple ? 's' : ''}`;
	}

	get getItemsMethodName(): typeof GetItemsMethodName | typeof ExtendedGetItemsMethodName {
		return GetItemsMethodName;
	}

	get hasItemsPropOnMk(): typeof HasItemsPropOnMk | typeof ExtendedHasItemsPropOnMk {
		return HasItemsPropOnMk;
	}

	get hasItemsPropOnMetricSetup():
		typeof HasItemsPropOnMetricSetup | typeof ExtendedHasItemsPropOnMetricSetup {
		return HasItemsPropOnMetricSetup;
	}

	get extraOptions(): LabelValuePairString[] {
		// extended in select-units
		return [];
	}

	get selectedSingle() {
		return this.args.selected as string | null;
	}

	get selectedMultiple() {
		return this.args.selected as string[];
	}

	@action
	onCloseSingle(_fn: unknown, selected: string | null | undefined) {
		const selectedOld = this.args.selected as string | null | undefined;
		if (!equal(selectedOld, selected)) {
			const onChange = this.args.onChange as ArgsSingle['onChange'];
			onChange(selected!);
		}
	}

	@action
	onCloseMultiple(_fn: unknown, selected?: string[]) {
		const selectedOld = this.args.selected as string[];
		if (!equal(selectedOld, selected)) {
			const onChange = this.args.onChange as ArgsMultiple['onChange'];
			onChange(selected || []);
		}
	}

	@action
	beforeChangeSingle(currentValue: string, newValue: string, selectElement: JQuery<HTMLElement>) {
		const beforeChange = this.args.beforeChange as ArgsSingle['beforeChange'];
		beforeChange?.(currentValue, newValue, selectElement);
		return false;
	}

	@action
	beforeChangeMultiple(
		currentValue: string[],
		newValue: string[],
		selectElement: JQuery<HTMLElement>,
	) {
		const beforeChange = this.args.beforeChange as ArgsMultiple['beforeChange'];
		beforeChange?.(currentValue, newValue, selectElement);
		return false;
	}

	updateOptions(options: Options = [], skipEqualCheck = false) {
		if (this.extraOptions) {
			this.extraOptions.reverse().forEach((item) => {
				if (!options.find((i) => i.value === item.value)) {
					options.unshift(item);
				}
			});
		}

		let selected: string[] = [];
		if (this.args.isMultiple) {
			selected = (this.args.selected as string[]) || [];
		} else if (this.args.selected) {
			selected = [this.args.selected as string];
		}

		// the constructor must not read the tracked options it is about to set
		const previousOptions = skipEqualCheck ? [] : this.options;
		selected.forEach((item) => {
			if (!this.isOnList(item, options)) {
				options.push({
					value: item,
					label: previousOptions.find((i) => i.value === item)?.label ?? item,
				});
			}
		});

		const disabledOptions: string[] = this.args.disabledItemsList || [];
		if (disabledOptions.length) {
			options = options.map((item) => {
				let itemData;
				if (disabledOptions.includes(item.value)) {
					itemData = Object.assign({}, item);
					itemData.disabled = true;
				} else {
					itemData = item;
				}
				return itemData;
			});
		}

		if (skipEqualCheck || !equal(this.options, options)) {
			this.options = options;
			this.onOptionsUpdate();
		}
	}

	onOptionsUpdate() {
		// extended in select-attributes-with-colors
	}

	@action
	onOpen(cb: () => void) {
		if (this.isLoading) {
			return false;
		}

		let promise: Promise<Options>;
		if (this.args.loadOptions) {
			promise = this.args.loadOptions();
		} else {
			const metricSetup = this.args.metricSetupForQuery;

			if (!metricSetup) {
				return false;
			}

			const shouldSkipRequest =
				!metricSetup.metric ||
				!metricSetup[this.hasItemsPropOnMetricSetup] ||
				!!metricSetup.metric?.isGeneric;

			if (shouldSkipRequest) {
				return false;
			}

			promise = this.mmMetricsService[this.getItemsMethodName](
				metricSetup.metricSetupDataForFetchDimensionValues,
				this.args.dateRangeForQuery || undefined,
				this.args.context,
			).then((list) => {
				return list.map((item) => {
					return {
						value: item,
						label: item,
					};
				});
			});
		}

		this.isLoading = true;

		void promise
			.then((items) => {
				next(() => {
					this.updateOptions(items);
					scheduleOnce('afterRender', this, cb);
				});
			})
			.catch()
			.finally(() => {
				this.isLoading = false;
			});

		return true; // requery every time we open a dropdown
	}

	@action
	onChange() {
		// do nothing. change will trigger on dropdown close
		// because we don't want to save changes before dropdown is closed
	}

	@action
	onCustomAdd(value: string) {
		// adding custom value
		this.options = [
			...this.options,
			{
				value: value,
				label: value,
			},
		];
		return true;
	}

	@action
	onMetricChange() {
		this.updateOptions();
	}

	@action
	onSelectedChange() {
		// in case an attribute was added externally that's not in options, we need to update options
		// also gets called when removing one of multiple attributes with (x)
		this.updateOptions(this.options);
	}

	@action
	onPropChange() {
		this.updateOptions(this.options);
	}

	@action
	setupView(node: HTMLElement) {
		this.selectNode = node as HTMLSelectElement;
	}
	<template>
		{{#if @isMultiple}}
			<FormSelectComponent
				name={{this.uniqueId}}
				@selected={{this.selectedMultiple}}
				@options={{this.options}}
				@onChange={{this.onChange}}
				@isNumeric={{false}}
				@isMultiple={{true}}
				@placeholder={{this.placeholder}}
				@floatlabelText={{this.floatlabelText}}
				@maxSelectionLength={{@maxSelectionLength}}
				@isLoading={{this.isLoading}}
				@isDisabled={{@isDisabled}}
				@isRequired={{@isRequired}}
				@tooltipOnSelectionFromProperty=""
				@onCustomAdd={{this.onCustomAdd}}
				@onOpen={{this.onOpen}}
				@onClose={{this.onCloseMultiple}}
				@beforeChange={{this.beforeChangeMultiple}}
				@showFloatlabel={{@showFloatlabel}}
				@theme={{@theme}}
				@size={{@size}}
				{{didInsert this.setupView}}
				{{didUpdate this.onMetricChange @metricSetupForQuery.metric}}
				{{didUpdate this.onSelectedChange @selected}}
				{{didUpdate this.onPropChange @isMultiple @disabledItemsList}}
			/>
		{{else}}
			<FormSelectComponent
				name={{this.uniqueId}}
				@selected={{this.selectedSingle}}
				@options={{this.options}}
				@onChange={{this.onChange}}
				@isNumeric={{false}}
				@placeholder={{this.placeholder}}
				@floatlabelText={{this.floatlabelText}}
				@maxSelectionLength={{@maxSelectionLength}}
				@isLoading={{this.isLoading}}
				@isDisabled={{@isDisabled}}
				@isRequired={{@isRequired}}
				@tooltipOnSelectionFromProperty=""
				@onCustomAdd={{this.onCustomAdd}}
				@onOpen={{this.onOpen}}
				@onClose={{this.onCloseSingle}}
				@beforeChange={{this.beforeChangeSingle}}
				@showFloatlabel={{@showFloatlabel}}
				@theme={{@theme}}
				@size={{@size}}
				{{didInsert this.setupView}}
				{{didUpdate this.onMetricChange @metricSetupForQuery.metric}}
				{{didUpdate this.onSelectedChange @selected}}
				{{didUpdate this.onPropChange @isMultiple @disabledItemsList}}
			/>
		{{/if}}
	</template>
}
